import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  Actividad,
  CorreoElectronico,
  createCorreoElectronico
} from '@rpe/registro-personas-empresas-lib';

import { Observable, Subscription } from 'rxjs';

import { DireccionesGeograficasFormComponent } from '../../ubicaciones/direcciones-geograficas/direcciones-geograficas-form/direcciones-geograficas-form.component';
import { selectActividadFromCurrentEmpresaByTipoActividad } from '../entity-actividades/entity-actividades.selectors';
import { State } from '../../vista-usuario/vista-usuario.state';
import {
  createEmptyLaboratorio,
  createEquipamiento,
  createLaboratorio,
  Equipamiento,
  EquipamientoFormInput,
  Laboratorio,
  LaboratorioFormInput
} from '../entity-laboratorios/laboratorios.model';
import { RegistroActividadLaboratorioEquipamientoFormComponent } from './registro-actividad-laboratorio-equipamiento-form/registro-actividad-laboratorio-equipamiento-form.component';
import { selectCurrentEmpresa } from '../../empresas/entity-mis-empresas/entity-current-empresa.selectors';
import { DatosContactoFormComponent } from '../../contactos/datos-contacto/datos-contacto-form/datos-contacto-form.component';
import {
  createDatosContacto,
  DatosContacto
} from '../../contactos/datos-contacto/datos-contacto.model';
import { DireccionGeografica } from '../../ubicaciones/direcciones-geograficas/direcciones-geografica.model';

@Component({
  selector: 'rpe-registro-actividad-laboratorio',
  templateUrl: './registro-actividad-laboratorio-form.component.html',
  styleUrls: ['./registro-actividad-laboratorio-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistroActividadLaboratorioFormComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() index = 0;

  @Input() set formInput(l: Laboratorio) {
    if (l && this.primeraVez) {
      this._laboratorio = l;
    }

    if (!l && this.primeraVez) {
      this._laboratorio = createEmptyLaboratorio();
    }

    if (this.primeraVez) {
      this.setForm();
    }

    this.primeraVez = false;
  }

  get formInput(): Laboratorio {
    return this._laboratorio;
  }

  @Output() newLaboratorio = new EventEmitter<boolean>();
  @Output() formStatusChanges = new EventEmitter<string>();
  @Output() formValueChanges = new EventEmitter<Laboratorio>();

  // Direccion geografica
  @ViewChild(DireccionesGeograficasFormComponent)
  direccionGeograficaForm: DireccionesGeograficasFormComponent;

  direccionGeograficaFormInput: DireccionGeografica = null;
  direccionGeografica: DireccionGeografica;
  direccionGeograficaValid: boolean = false;

  // Datos de contacto
  @ViewChild(DatosContactoFormComponent, { static: true })
  datosContactoForm: DatosContactoFormComponent;

  datosContactoFormInput: DatosContacto = null;
  datosContacto: DatosContacto;
  datosContactoStatus: string;

  @ViewChild(RegistroActividadLaboratorioEquipamientoFormComponent)
  equipamientoForm: RegistroActividadLaboratorioEquipamientoFormComponent;

  equipamientoFormInput: EquipamientoFormInput = null;
  equipamientoLaboratorio: Equipamiento;
  equipamientoValid: boolean = false;

  registroActividadLaboratorioFormGroup: FormGroup = this.fb.group({
    id: [null, []],
    nombre: [null, [Validators.required]],
    entidad_productiva_id: [null, [Validators.required]]
    // direccion_geografica: this.direccionGeograficaForm.createGroup()
  });
  _laboratoriosFormInput: LaboratorioFormInput;
  _laboratorio: Laboratorio;
  actividad$: Observable<Actividad>;
  subscription: Subscription;
  primeraVez: boolean = true;

  isLoaded = false;

  constructor(private fb: FormBuilder, private store: Store<State>, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.actividad$ = this.store.select(
      selectActividadFromCurrentEmpresaByTipoActividad(7)
    );

    this.subscription = this.store
      .select(selectCurrentEmpresa)
      .subscribe((e) => {
        if (!this._laboratorio.entidad_productiva_id) {
          this._laboratorio.entidad_productiva_id = e.id;
          this.registroActividadLaboratorioFormGroup.patchValue({
            entidad_productiva_id: e.id
          });
        }
      });

    this.registroActividadLaboratorioFormGroup.valueChanges.subscribe((_) => {
      this.emitirFormValues();
    });

    this.registroActividadLaboratorioFormGroup.statusChanges.subscribe((f) => {
      this.emitirFormStatus();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngAfterViewInit() {
    this.isLoaded = true;
  }

  datosContactoChange(dc: DatosContacto) {
    this.datosContacto = dc;
    this.emitirFormValues();
  }

  datosContactoStatusChange(s: string) {
    this.datosContactoStatus = s;
    this.emitirFormStatus();
  }

  direccionGeograficaChange(dg: any) {
    this.direccionGeografica = dg;
    this.emitirFormValues();
  }

  direccionGeograficaStatusChange(s: any) {
    this.direccionGeograficaValid = s === 'VALID';
    this.emitirFormStatus();
  }

  equipamientoChange(el: Equipamiento) {
    this.equipamientoLaboratorio = el;
    this.emitirFormValues();
  }

  equipamientoStatusChange(f: string) {
    this.equipamientoValid = f === 'VALID';
    this.emitirFormStatus();
  }

  checkChildrens() {
    this.direccionGeograficaForm.checkErrors();
    this.datosContactoForm.checkErrors();
    this.equipamientoForm.checkErrors();
  }

  creoLaboratorio(): Laboratorio {
    let correos: CorreoElectronico[] = [];
    if (this.datosContacto?.correos_electronico) {
      this.datosContacto.correos_electronico.forEach((c: CorreoElectronico) => {
        correos.push(
          createCorreoElectronico({
            id: c.id,
            direccion: c.direccion,
            tipo_correo_id: 1
          })
        );
      });
    } else {
      correos = null;
    }
    const l = createLaboratorio({
      ...this.registroActividadLaboratorioFormGroup.value,
      ...this.equipamientoLaboratorio,
      correos_electronico: correos,
      entidad_productiva_id: this._laboratorio.entidad_productiva_id,
      telefonos: this.datosContacto?.telefonos,
      direccion_geografica: this.direccionGeografica
    });

    return l;
  }

  emitirFormStatus() {
    let status = 'INVALID';

    if (
      this.registroActividadLaboratorioFormGroup.valid &&
      this.direccionGeograficaValid &&
      this.datosContactoStatus == 'VALID' &&
      this.equipamientoValid
    ) {
      status = 'VALID';
    }

    this.formStatusChanges.emit(status);
  }

  emitirFormValues() {
    const l = this.creoLaboratorio();

    this.formValueChanges.emit(l);
  }

  agregarNuevoLaboratorio() {
    this.newLaboratorio.emit(true);
  }

  eliminarLaboratorio() {
    this.newLaboratorio.emit(false);
  }

  checkErrors() {
    this.registroActividadLaboratorioFormGroup.markAllAsTouched();
    this.checkChildrens();
    this.cdr.markForCheck();
  }

  setForm() {
    // Form
    this.registroActividadLaboratorioFormGroup.patchValue(
      {
        id: this._laboratorio.id,
        nombre: this._laboratorio.nombre,
        entidad_productiva_id: this._laboratorio.entidad_productiva_id
      },
      { emitEvent: false }
    );
    // Direccion geografica
    this.direccionGeografica = { ...this._laboratorio.direccion_geografica };
    this.direccionGeograficaFormInput = this.direccionGeografica;
    this.datosContacto = createDatosContacto({
      telefonos: this._laboratorio.telefonos,
      correos_electronico: this._laboratorio.correos_electronico
    });
    this.datosContactoFormInput = this.datosContacto;
    this.equipamientoLaboratorio = createEquipamiento({
      balanzas: this._laboratorio.balanzas,
      germinadores: this._laboratorio.germinadores,
      divisores_muestra: this._laboratorio.divisores_muestra,
      equipamientos: this._laboratorio.equipamientos,
      analisis_especie: this._laboratorio.analisis_especie
    });
    this.equipamientoFormInput = {
      equipamiento: this.equipamientoLaboratorio,
      laboratorioId: this._laboratorio.id
    };
  }
}
